// Payment voucher status types
export type PaymentVoucherStatus = 'created' | 'confirmed' | 'cancelled';

// Payment voucher type options
export type PaymentVoucherType = 'supplier_payment' | 'employee_payment' | 'cash' | 'transfer';

// Receiver type options
export type ReceiverType = 'supplier' | 'employee' | 'other';

// Payment method options
export type PaymentMethod = 'cash' | 'transfer';

// Supplier response interface (matching backend DTO)
export interface SupplierResponse {
  id: string;
  code: string;
  name: string;
  contact_person?: string;
  email?: string;
  phone: string;
  tax?: string;
  address?: string;
  status?: string;
  total_debt?: number;
  payment_status?: string;
  last_payment_date?: string;
  note?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Employee response interface (placeholder for future implementation)
export interface EmployeeResponse {
  id: string;
  code: string;
  name: string;
  email?: string;
  phone?: string;
  position?: string;
  department?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Status history entry
export interface StatusHistoryEntry {
  status: PaymentVoucherStatus;
  note?: string;
  created_at: string;
  created_by: string;
}

// Base payment voucher interface
export interface PaymentVoucher {
  id: string;
  code: string;
  payment_date: string;
  type: ReceiverType;
  receiver_id: string;
  payment_type: PaymentVoucherType;
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  amount: number;
  current_status: StatusHistoryEntry;
  status_history: StatusHistoryEntry[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

// UI-specific payment voucher interface with additional computed fields
export interface PaymentVoucherUI extends PaymentVoucherResponse {
  // Additional computed fields for UI display
  status_display?: string;
  receiver_name?: string;
  formatted_amount?: string;
}

// API Request types
export interface CreatePaymentVoucherRequest {
  payment_date: string;
  type: ReceiverType;
  receiver_id?: string;
  receiver_name?: string;
  payment_type: PaymentVoucherType;
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  amount: number;
}

export interface UpdatePaymentVoucherRequest {
  payment_date?: string;
  type?: ReceiverType;
  receiver_id?: string;
  receiver_name?: string;
  payment_type?: PaymentVoucherType;
  payment_method?: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  amount?: number;
}

// API Response types
export interface StatusHistoryResponse {
  status: PaymentVoucherStatus;
  note?: string;
  created_at: string;
  created_by: string;
}

export interface WarehouseReceiptResponse {
  id: string;
  code: string;
  supplier_id: string;
  receipt_date: string;
  status: string;
  note?: string;
  total_amount: number;
  paid_amount: number;
  receipt_lines_id: string[];
  current_status: StatusHistoryResponse;
  status_history: StatusHistoryResponse[];
  payment_status: StatusHistoryResponse;
  payment_status_history: StatusHistoryResponse[];
  created_by: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentVoucherResponse {
  id: string;
  code: string;
  payment_date: string;
  type: ReceiverType;
  receiver_id: string;
  receiver_name?: string;
  payment_type: PaymentVoucherType;
  payment_method: PaymentMethod;
  account_number?: string;
  transfer_note?: string;
  note?: string;
  reason?: string;
  amount: number;
  warehouse_receiver_ids?: string[]; // Array of warehouse receipt codes
  warehouse_receipts?: WarehouseReceiptResponse[]; // Full warehouse receipt details
  current_status: StatusHistoryResponse;
  status_history: StatusHistoryResponse[];
  // Receiver information (nested object from API)
  receiver?: SupplierResponse | EmployeeResponse;
  supplier?: SupplierResponse;
  user?: EmployeeResponse;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

// List query filters
export interface PaymentVoucherFilters {
  payment_type?: PaymentVoucherType;
  payment_method?: PaymentMethod;
  receiver_type?: ReceiverType;
  receiver_id?: string;
  status?: PaymentVoucherStatus;
  date_from?: string;
  date_to?: string;
}

export interface PaymentVoucherListQuery extends PaymentVoucherFilters {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// Paginated response
export interface PaginatedPaymentVoucherResponse {
  data: PaymentVoucherResponse[];
  total: number;
  page: number;
  page_size: number;
}

// Status counts for filter tabs
export interface PaymentVoucherStatusCounts {
  all: number;
  created: number;
  confirmed: number;
  cancelled: number;
}

// Form validation types
export interface PaymentVoucherFormErrors {
  payment_date?: string;
  type?: string;
  receiver_id?: string;
  payment_type?: string;
  payment_method?: string;
  account_number?: string;
  transfer_note?: string;
  amount?: string;
}

// Constants for dropdown options
export const PAYMENT_VOUCHER_TYPES: { value: PaymentVoucherType; label: string }[] = [
  { value: 'supplier_payment', label: 'paymentVoucher.paymentType.supplier_payment' },
  { value: 'employee_payment', label: 'paymentVoucher.paymentType.employee_payment' },
  { value: 'cash', label: 'paymentVoucher.paymentType.cash' },
  { value: 'transfer', label: 'paymentVoucher.paymentType.transfer' }
];

export const RECEIVER_TYPES: { value: ReceiverType; label: string }[] = [
  { value: 'supplier', label: 'paymentVoucher.receiverType.supplier' },
  { value: 'employee', label: 'paymentVoucher.receiverType.employee' },
  { value: 'other', label: 'paymentVoucher.receiverType.other' }
];

export const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'paymentVoucher.paymentMethod.cash' },
  { value: 'transfer', label: 'paymentVoucher.paymentMethod.transfer' }
];

export const PAYMENT_VOUCHER_STATUS_OPTIONS: { value: PaymentVoucherStatus; label: string }[] = [
  { value: 'created', label: 'paymentVoucher.status.created' },
  { value: 'confirmed', label: 'paymentVoucher.status.confirmed' },
  { value: 'cancelled', label: 'paymentVoucher.status.cancelled' }
];

// Helper functions
export const getStatusColor = (status: PaymentVoucherStatus): string => {
  switch (status) {
    case 'created':
      return 'yellow';
    case 'confirmed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'gray';
  }
};

export const getStatusLabel = (status: PaymentVoucherStatus): string => {
  const option = PAYMENT_VOUCHER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option?.label || status;
};

export const formatPaymentAmount = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount);
};
